import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';

/**
 * Watches the service worker for `controllerchange` (a new SW is ready) and
 * surfaces a "New version available — Reload" toast. Reload only happens after
 * the user opts in (per plan §3.5 — never rip state out from under an
 * in-flight session).
 */
export function UpdateToast() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegistered(_registration) {
      // Manual registration is wired via src/pwa/register.ts as well; this hook
      // is the React-friendly surface for the update lifecycle.
    },
    onRegisterError(err) {
      console.error('[bazi/web] SW registration failed', err);
    },
  });

  useEffect(() => {
    setVisible(needRefresh);
  }, [needRefresh]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 z-40 flex items-center gap-3 rounded-2xl bg-ink-800/95 px-4 py-3 text-warm-100 shadow-card backdrop-blur md:left-auto md:right-4 md:max-w-sm"
      style={{ bottom: 'max(0.75rem, var(--safe-bottom))' }}
    >
      <RefreshCw className="h-5 w-5 shrink-0 text-gold-500" aria-hidden="true" />
      <div className="flex-1 text-sm">
        {t('update.available', 'New version available')}
      </div>
      <button
        type="button"
        onClick={() => {
          setNeedRefresh(false);
          void updateServiceWorker(true);
        }}
        className="cursor-pointer rounded-xl bg-jade-500 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-jade-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade-500"
      >
        {t('update.reload', 'Reload')}
      </button>
    </div>
  );
}
